// Villa San Biagio - Modern JavaScript Entry Point
// Migrated from jQuery to Vanilla JavaScript

// Import modern libraries
import Swiper from "swiper";
import { Navigation, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

import GLightbox from "glightbox";
import "glightbox/dist/css/glightbox.css";

import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";

// Esponi Swiper globalmente per lo script inline della gallery
window.Swiper = Swiper;
window.SwiperModules = { Navigation, Autoplay, EffectFade };

// ==========================================
// Utility Functions
// ==========================================

/**
 * Debounce function to limit how often a function can fire
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
function debounce(func, wait = 10) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function to limit function execution rate
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
function throttle(func, limit = 100) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Wait for DOM to be ready
document.addEventListener("DOMContentLoaded", function () {
  "use strict";

  // Initialize all components
  initMenuToggle();
  initDropdowns();
  initCarousels();
  initLightbox();
  initDateTimePickers();
  initSmoothScroll();
  initScrollEffects();
  initParallax();
  initAOS();
});

// ==========================================
// Menu Toggle
// ==========================================
function initMenuToggle() {
  const menuToggle = document.querySelector(".site-menu-toggle");
  const navbar = document.querySelector(".js-site-navbar");

  if (!menuToggle || !navbar) return;

  menuToggle.addEventListener("click", function () {
    const body = document.body;
    const isOpen = body.classList.contains("menu-open");

    if (isOpen) {
      // Close menu
      menuToggle.classList.remove("open");
      body.classList.remove("menu-open");

      // Fade out
      navbar.style.opacity = "0";
      setTimeout(() => {
        navbar.style.display = "none";
      }, 400); // Match transition duration
    } else {
      // Open menu
      menuToggle.classList.add("open");
      body.classList.add("menu-open");

      // Fade in
      navbar.style.display = "block";
      // Force reflow to ensure display change is applied before opacity
      navbar.offsetHeight;
      navbar.style.opacity = "1";
    }
  });
}

// ==========================================
// Dropdown Hover Effects
// ==========================================
function initDropdowns() {
  const dropdowns = document.querySelectorAll("nav .dropdown");

  dropdowns.forEach((dropdown) => {
    dropdown.addEventListener("mouseenter", function () {
      this.classList.add("show");
      const link = this.querySelector("> a");
      if (link) link.setAttribute("aria-expanded", "true");

      const menu = this.querySelector(".dropdown-menu");
      if (menu) menu.classList.add("show");
    });

    dropdown.addEventListener("mouseleave", function () {
      this.classList.remove("show");
      const link = this.querySelector("> a");
      if (link) link.setAttribute("aria-expanded", "false");

      const menu = this.querySelector(".dropdown-menu");
      if (menu) menu.classList.remove("show");
    });
  });
}

// ==========================================
// Carousels (Swiper)
// ==========================================
function initCarousels() {
  // Home slider
  const homeSlider = document.querySelector(".home-slider");
  if (homeSlider) {
    new Swiper(".home-slider", {
      modules: [Navigation, Autoplay, EffectFade],
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      effect: "fade",
      fadeEffect: {
        crossFade: true,
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      speed: 1000,
    });
  }

  // Carousel 1
  const carousel1 = document.querySelector(".js-carousel-1");
  if (carousel1) {
    new Swiper(".js-carousel-1", {
      modules: [Navigation, Autoplay],
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      spaceBetween: 20,
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      breakpoints: {
        0: {
          slidesPerView: 1,
          navigation: false,
        },
        600: {
          slidesPerView: 2,
        },
        1000: {
          slidesPerView: 3,
        },
      },
    });
  }

  // Carousel 2
  const carousel2 = document.querySelector(".js-carousel-2");
  if (carousel2) {
    new Swiper(".js-carousel-2", {
      modules: [Navigation, Autoplay],
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      spaceBetween: 20,
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      breakpoints: {
        0: {
          slidesPerView: 1,
        },
        600: {
          slidesPerView: 2,
        },
        1000: {
          slidesPerView: 3,
        },
      },
    });
  }
}

// ==========================================
// Lightbox (GLightbox)
// ==========================================
function initLightbox() {
  const lightbox = GLightbox({
    selector: "[data-fancybox], [data-lightbox]",
    touchNavigation: true,
    loop: true,
    autoplayVideos: true,
  });
}

// ==========================================
// Date & Time Pickers (Flatpickr)
// ==========================================
function initDateTimePickers() {
  // Date picker for m_date
  const mDate = document.getElementById("m_date");
  if (mDate) {
    flatpickr("#m_date", {
      dateFormat: "m/d/Y",
      minDate: "today",
    });
  }

  // Date pickers for check-in/check-out
  const checkinDate = document.getElementById("checkin_date");
  const checkoutDate = document.getElementById("checkout_date");

  if (checkinDate || checkoutDate) {
    flatpickr("#checkin_date, #checkout_date", {
      dateFormat: "d M, Y",
      minDate: "today",
      altInput: true,
      altFormat: "d F, Y",
    });
  }

  // Time picker
  const mTime = document.getElementById("m_time");
  if (mTime) {
    flatpickr("#m_time", {
      enableTime: true,
      noCalendar: true,
      dateFormat: "H:i",
      time_24hr: false,
    });
  }
}

// ==========================================
// Smooth Scrolling
// ==========================================
function initSmoothScroll() {
  const smoothScrollLinks = document.querySelectorAll(
    'a.smoothscroll[href^="#"], a.mouse[href^="#"]',
  );

  smoothScrollLinks.forEach((link) => {
    link.addEventListener("click", function (e) {
      e.preventDefault();
      const targetId = this.getAttribute("href");
      const target = document.querySelector(targetId);

      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    });
  });
}

// ==========================================
// Scroll Effects (Sticky Header, Back to Top)
// ==========================================
function initScrollEffects() {
  const header = document.querySelector(".js-site-header");
  const backToTop = document.querySelector(".js-top");

  // Optimized scroll handler using throttle for better performance
  const handleScroll = throttle(() => {
    const scrollPosition = window.scrollY;
    const threshold = 200;

    // Sticky header
    if (header) {
      if (scrollPosition > threshold) {
        header.classList.add("scrolled");
      } else {
        header.classList.remove("scrolled");
      }
    }

    // Back to top button
    if (backToTop) {
      if (scrollPosition > threshold) {
        backToTop.classList.add("active");
      } else {
        backToTop.classList.remove("active");
      }
    }
  }, 100); // Throttle to max once per 100ms

  window.addEventListener("scroll", handleScroll, { passive: true });

  // Back to top click handler
  const goToTop = document.querySelector(".js-gotop");
  if (goToTop) {
    goToTop.addEventListener("click", function (e) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  }
}

// ==========================================
// Initialize AOS (already vanilla JS)
// ==========================================
function initAOS() {
  if (typeof AOS !== "undefined") {
    AOS.init({
      duration: 1000,
    });
  }
}

// ==========================================
// CSS-based Parallax Effect
// ==========================================
function initParallax() {
  // Apply CSS parallax to elements with data-stellar-background-ratio
  const parallaxElements = document.querySelectorAll(
    "[data-stellar-background-ratio]",
  );

  parallaxElements.forEach((element) => {
    // CSS handles the parallax via background-attachment: fixed
    // This is applied in modern-animations.css
    element.classList.add("parallax-bg");
  });

  // For more advanced parallax, you can use requestAnimationFrame
  // Uncomment below if you need custom parallax speed control

  /*
  let ticking = false;
  
  function updateParallax() {
    const scrolled = window.scrollY;
    
    parallaxElements.forEach(element => {
      const speed = parseFloat(element.getAttribute('data-stellar-background-ratio')) || 0.5;
      const yPos = -(scrolled * speed);
      element.style.backgroundPosition = `center ${yPos}px`;
    });
    
    ticking = false;
  }
  
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(updateParallax);
      ticking = true;
    }
  }, { passive: true });
  */
}
